import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { Button } from '../../button/button';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';

type Props = {
  dialog: ReturnType<typeof useDialog>;
  gcpName: string;
  onConfirm: () => Promise<void>;
};

export const DeleteGcpConfirmationDialog = ({ dialog, gcpName, onConfirm }: Props) => {
  const { t } = useTranslation();
  const { handleError, buildErrorList } = useErrorHandling();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      dialog.hide();
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const errorList = buildErrorList();

  return (
    <Dialog closeIcon={true} onClose={dialog.hide}>
      {{
        header: t('gcpList.deleteGcp', { ns: 'cloudProcessing' }),
        content: (
          <Stack spacing={1}>
            <span>
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="gcpList.deleteConfirmation"
                ns="cloudProcessing"
                values={{ gcpName }}
              />
            </span>
            <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'exclamation'] }}>
              <div>{t('gcpList.deleteMarkersWarning', { ns: 'cloudProcessing' })}</div>
            </InfoBox>
            {errorList}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="error"
                disabled={isDeleting}
                leftIcon={{ icon: ['fas', 'trash-alt'] }}
                loading={isDeleting}
                variant="contained"
                onClick={handleConfirm}
              >
                {t('delete', { ns: 'common' })}
              </Button>
              <Button disabled={isDeleting} variant="text" onClick={dialog.hide}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
